import {
  ErrorComponentProps,
  Navigate,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect
} from '@tanstack/react-router'
import { accountSettingsRoute, routeAuthTree } from 'auth'
import { routeAdverseMediaTree } from 'bank-adverse-media'
import { routeAlertsTree } from 'bank-alerts'
import { routeAnnualReviewsTree } from 'bank-annual-review'
import { routeBSATree } from 'bank-bsa-reporting'
import { routeClientUsersTree } from 'bank-client-users'
import { routeCompaniesTree } from 'bank-companies'
import { routeConnectionsTree } from 'bank-connections'
import { routeContactsTree } from 'bank-contacts'
import { routeDocumentsTree } from 'bank-documents'
import { routeHistoryTree } from 'bank-entity-history'
import { routeInternalTransfersTree } from 'bank-internal-transfers'
import { routeInvoicesTree } from 'bank-invoices'
import { routeLicensesTree } from 'bank-licenses'
import { routeNextCloudTree } from 'bank-nextcloud-login'
import { routeOnboardingTree } from 'bank-onboarding'
import { routeOnboardingDocumentsTree } from 'bank-onboarding-documents'
import { routeHighRiskTree, routeMRTree, routeQRTree } from 'bank-quarterly-report'
import { routeSalesDataTree } from 'bank-salesdata'
import { TanStackRouterDevTools } from 'commons/utils'
import { Logger } from 'commons/utils/logger'
import { routeReleaseNotesRoute } from 'modules/releaseNotes'
import { BPLayout } from 'ui/components/BPLayout'
import { TechnicalIssues } from 'ui/temp'
import { UploadProgressProvider, UploadProgressWidget } from 'ui/temp/UploadProgress'

import { routeBankAccountsTree } from '@orbit/bank-accounts'
import { bankCrbMonitorRoute } from '@orbit/bank-crb-monitor'
import { bankCustomerVendorRoutingNode } from '@orbit/bank-customer-vendor'
import { bankDocumentTemplatesRoute } from '@orbit/bank-document-templates'
import { routeReportsTree } from '@orbit/bank-reports'
import { bankUsersRoutingNode } from '@orbit/bank-users'

import { NotFound } from '../../404/components/NotFound'

const rootRoute = createRootRouteWithContext<BPRoutingContext>()({
  component: () => (
    <>
      <Outlet />
      <TanStackRouterDevTools position="top-right" />
    </>
  ),
  notFoundComponent: () => <NotFound />,
  errorComponent: (props: ErrorComponentProps) => {
    Logger.getInstance().error(props.error.message, props.error)
    return <TechnicalIssues error={(props.error as any).httpCode} />
  }
})

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: () => (
    <UploadProgressProvider>
      <BPLayout>
        <Outlet />
      </BPLayout>
      <UploadProgressWidget />
    </UploadProgressProvider>
  ),
  beforeLoad: ({ location, context }) => {
    if (!context.auth?.hasSession) {
      throw redirect({
        to: '/auth/login',
        search: {
          returnUrl: location.pathname !== '/' ? location.pathname : undefined
        }
      })
    }
  }
})

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  component: () => <Outlet />
})

const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: () => <Navigate to="/relationships" />
})

const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([routeAuthTree(authLayoutRoute)]),
  appLayoutRoute.addChildren([
    routeReleaseNotesRoute(appLayoutRoute),
    routeCompaniesTree(appLayoutRoute),
    routeContactsTree(appLayoutRoute),
    routeAnnualReviewsTree(appLayoutRoute),
    routeBankAccountsTree(appLayoutRoute),
    routeInternalTransfersTree(appLayoutRoute),
    routeDocumentsTree(appLayoutRoute),
    routeSalesDataTree(appLayoutRoute),
    routeMRTree(appLayoutRoute),
    routeQRTree(appLayoutRoute),
    routeAlertsTree(appLayoutRoute),
    routeLicensesTree(appLayoutRoute),
    routeClientUsersTree(appLayoutRoute),
    routeConnectionsTree(appLayoutRoute),
    routeReportsTree(appLayoutRoute),
    routeAdverseMediaTree(appLayoutRoute),
    bankUsersRoutingNode(appLayoutRoute),
    bankCrbMonitorRoute(appLayoutRoute),
    bankCustomerVendorRoutingNode(appLayoutRoute),
    routeHistoryTree(appLayoutRoute),
    routeInvoicesTree(appLayoutRoute),
    routeOnboardingTree(appLayoutRoute),
    routeOnboardingDocumentsTree(appLayoutRoute),
    accountSettingsRoute(appLayoutRoute),
    routeBSATree(appLayoutRoute),
    routeNextCloudTree(appLayoutRoute),
    bankDocumentTemplatesRoute(appLayoutRoute),
    routeHighRiskTree(appLayoutRoute),
    indexRoute
  ])
])

export const BPRouter = createRouter({
  routeTree,
  context: {
    accessibleRoutingFlags: undefined!,
    showConnectionsSectionMenu: false,
    auth: undefined!,
    queryClient: undefined!
  },
  defaultPendingMs: 0,
  defaultGcTime: 0,
  scrollRestoration: true,
  defaultHashScrollIntoView: { behavior: 'smooth' }
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof BPRouter
  }
}
